import { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, User, Search, Loader2 } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';

const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/api/news').then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return posts;
    return posts.filter((p) =>
      [p.title, p.excerpt, p.author].some((v) => (v || '').toLowerCase().includes(s))
    );
  }, [posts, q]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
            News & Announcements
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed mb-8">
            Stay updated with the latest news, announcements, and stories from our community.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <Input
              type="text"
              placeholder="Search news..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-12 py-6 text-base"
              data-testid="news-search"
            />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">No posts found.</div>
      ) : (
        <>
          {/* Featured */}
          <section className="py-12 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Card className="overflow-hidden shadow-elevated cursor-pointer" onClick={() => setSelected(featured)} data-testid={`news-featured-${featured.id}`}>
                <div className="grid lg:grid-cols-2 gap-0">
                  {featured.featured_image && (
                    <div className="h-96 lg:h-auto">
                      <img src={mediaUrl(featured.featured_image)} alt={featured.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <Badge className="bg-primary/10 text-primary hover:bg-primary/20 w-fit mb-4">Featured</Badge>
                    <h2 className="text-3xl lg:text-4xl font-serif font-bold text-foreground mb-4">{featured.title}</h2>
                    <p className="text-muted-foreground leading-relaxed mb-6">{featured.excerpt}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center"><Calendar size={16} className="mr-2" />{new Date(featured.created_at).toLocaleDateString()}</div>
                      {featured.author && <div className="flex items-center"><User size={16} className="mr-2" />{featured.author}</div>}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </section>

          {/* Grid */}
          {rest.length > 0 && (
            <section className="py-12 bg-background">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((p) => (
                    <Card
                      key={p.id}
                      className="overflow-hidden hover:shadow-elevated transition-elegant group cursor-pointer"
                      onClick={() => setSelected(p)}
                      data-testid={`news-card-${p.id}`}
                    >
                      {p.featured_image && (
                        <div className="aspect-video overflow-hidden">
                          <img src={mediaUrl(p.featured_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <CardContent className="pt-6 pb-6 space-y-3">
                        <h3 className="text-xl font-serif font-semibold text-foreground line-clamp-2">{p.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center"><Calendar size={14} className="mr-1" />{new Date(p.created_at).toLocaleDateString()}</div>
                          {p.author && <span>· {p.author}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground">
                {new Date(selected.created_at).toLocaleDateString()} {selected.author && `· ${selected.author}`}
              </div>
              {selected.featured_image && (
                <img src={mediaUrl(selected.featured_image)} alt="" className="w-full rounded-md max-h-96 object-cover" />
              )}
              <div
                className="prose prose-sm max-w-none [&_h2]:font-serif [&_h2]:text-xl [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-3 [&_blockquote]:italic [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: selected.body || `<p>${selected.excerpt || ''}</p>` }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default NewsPage;
